import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Plus,
  Search,
  Pencil,
  Lock,
  Unlock,
  Trash2,
  X,
  Mail,
  Phone,
  Shield,
  Database,
  Users,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  CREATE_FSR,
  DELETE_FSR,
  GET_FSRS,
  GET_FSR_SUMMARY,
  RESET_FSR_PASSWORD,
  SET_FSR_STATUS,
  UPDATE_FSR,
} from '../graphql/queries';
import { Modal } from '../components/Modal';

type FsrStatus = 'active' | 'inactive' | 'suspended';

interface Fsr {
  id: string;
  name: string;
  employeeId: string;
  email: string;
  mobile: string | null;
  status: FsrStatus;
  reportingTo: 'manufacturer' | 'distributor';
  distributorId: string | null;
  catalogAccess: 'full' | 'collection' | 'selective';
  catalogCollections: string[];
  avatar: string | null;
  color: string | null;
  lastSeenAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

type FsrTab = 'all' | 'direct' | 'distributor';

const STATUS_PILL: Record<FsrStatus, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-700',
  suspended: 'bg-red-100 text-red-700',
};

const StatusPill: React.FC<{ status: FsrStatus }> = ({ status }) => (
  <span className={`status-pill ${STATUS_PILL[status]}`}>{status.toUpperCase()}</span>
);

interface FormState {
  name: string;
  email: string;
  mobile: string;
  reportingTo: 'manufacturer' | 'distributor';
  catalogAccess: 'full' | 'collection';
}

const emptyForm: FormState = {
  name: '',
  email: '',
  mobile: '',
  reportingTo: 'manufacturer',
  catalogAccess: 'full',
};

export const FieldUsers: React.FC<{ portal: string }> = ({ portal }) => {
  const [tab, setTab] = useState<FsrTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedFsr, setSelectedFsr] = useState<Fsr | null>(null);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [createdEmployeeId, setCreatedEmployeeId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  type PendingAction =
    | { kind: 'suspend'; fsr: Fsr }
    | { kind: 'activate'; fsr: Fsr }
    | { kind: 'delete'; fsr: Fsr }
    | null;
  const [pending, setPending] = useState<PendingAction>(null);

  const reportingFilter = tab === 'direct' ? 'manufacturer' : tab === 'distributor' ? 'distributor' : undefined;

  const { data, loading, error, refetch } = useQuery(GET_FSRS, {
    variables: {
      page: 1,
      limit: 100,
      search: searchQuery || undefined,
      reportingTo: reportingFilter,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: summaryData, refetch: refetchSummary } = useQuery(GET_FSR_SUMMARY, {
    fetchPolicy: 'cache-and-network',
  });

  const refresh = () => {
    refetch();
    refetchSummary();
  };

  const [createFsr, { loading: creating }] = useMutation(CREATE_FSR, { onCompleted: refresh });
  const [updateFsr] = useMutation(UPDATE_FSR, { onCompleted: refresh });
  const [deleteFsr] = useMutation(DELETE_FSR, { onCompleted: refresh });
  const [setFsrStatus] = useMutation(SET_FSR_STATUS, { onCompleted: refresh });
  const [resetPassword] = useMutation(RESET_FSR_PASSWORD);

  const fsrs: Fsr[] = data?.getFsrs?.fsrs ?? [];
  const summary = summaryData?.getFsrSummary ?? {
    total: 0,
    active: 0,
    inactive: 0,
    suspended: 0,
    direct: 0,
    underDistributor: 0,
    fsrLimit: null,
    isUnlimitedFsrs: false,
    isFreeAccount: false,
    isSubscriptionActive: false,
    seatsRemaining: null,
  };
  const seatsUsed = summary.active + summary.inactive;
  const seatGate: {
    label: string;
    color: 'emerald' | 'amber' | 'red' | 'gray';
    blocked: boolean;
    banner: string | null;
  } = (() => {
    // Free accounts skip the subscription-active check but still respect
    // the seat cap, so the order is: subscription gate (paid only) →
    // unlimited → limit check.
    if (!summary.isFreeAccount && !summary.isSubscriptionActive) {
      return {
        label: 'Subscription inactive',
        color: 'red',
        blocked: true,
        banner:
          'Your subscription is inactive. Renew it from Settings → Subscription before adding more field users.',
      };
    }
    if (summary.isUnlimitedFsrs) {
      return {
        label: summary.isFreeAccount ? 'Free account · unlimited seats' : 'Unlimited seats',
        color: 'emerald',
        blocked: false,
        banner: null,
      };
    }
    if (typeof summary.fsrLimit !== 'number') {
      return {
        label: 'No limit on file',
        color: 'amber',
        blocked: true,
        banner: summary.isFreeAccount
          ? 'No FSR seat limit set for this free account yet. Ask the platform admin to configure it.'
          : 'No FSR seat limit on file yet. Wait for the subscription service to sync, or contact support.',
      };
    }
    const remaining = summary.fsrLimit - seatsUsed;
    const baseLabel = `${seatsUsed} of ${summary.fsrLimit} seats used`;
    const label = summary.isFreeAccount ? `Free account · ${baseLabel}` : baseLabel;
    if (remaining <= 0) {
      return {
        label,
        color: 'red',
        blocked: true,
        banner: summary.isFreeAccount
          ? `This free account is capped at ${summary.fsrLimit} field user${
              summary.fsrLimit === 1 ? '' : 's'
            }. Ask the platform admin to raise the limit.`
          : `You've reached your plan's limit of ${summary.fsrLimit} field user${
              summary.fsrLimit === 1 ? '' : 's'
            }. Upgrade your plan to add more.`,
      };
    }
    return { label, color: remaining <= 2 ? 'amber' : 'gray', blocked: false, banner: null };
  })();

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleSelectAll = () =>
    setSelectedIds((prev) => (prev.length === fsrs.length ? [] : fsrs.map((f) => f.id)));

  const openAdd = () => {
    setForm(emptyForm);
    setCreatedPassword(null);
    setCreatedEmployeeId(null);
    setFormError(null);
    setIsAddPanelOpen(true);
  };

  const submitCreate = async () => {
    setFormError(null);
    if (!form.name.trim() || !form.email.trim()) {
      setFormError('Name and email are required');
      return;
    }
    const { data: res } = await createFsr({
      variables: {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        mobile: form.mobile.trim() || null,
        reportingTo: form.reportingTo,
        catalogAccess: form.catalogAccess,
      },
    });
    const out = res?.createFsr;
    if (out?.error) {
      setFormError(out.message || 'Failed to create');
      return;
    }
    setCreatedEmployeeId(out?.fsr?.employeeId || null);
    setCreatedPassword(out?.generatedPassword || null);
  };

  const askSuspendToggle = (fsr: Fsr) =>
    setPending({ kind: fsr.status === 'suspended' ? 'activate' : 'suspend', fsr });

  const askDelete = (fsr: Fsr) => setPending({ kind: 'delete', fsr });

  const confirmPending = async () => {
    if (!pending) return;
    if (pending.kind === 'delete') {
      await deleteFsr({ variables: { id: pending.fsr.id } });
    } else {
      const next: FsrStatus = pending.kind === 'suspend' ? 'suspended' : 'active';
      await setFsrStatus({ variables: { ids: [pending.fsr.id], status: next } });
    }
    setPending(null);
    setIsDetailPanelOpen(false);
  };

  const submitBulkStatus = async (status: FsrStatus) => {
    if (selectedIds.length === 0) return;
    await setFsrStatus({ variables: { ids: selectedIds, status } });
    setSelectedIds([]);
  };

  const submitResetPassword = async (id: string) => {
    const { data: res } = await resetPassword({ variables: { id } });
    const pwd = res?.resetFsrPassword?.generatedPassword;
    if (pwd) window.prompt('New password — copy now (shown only once):', pwd);
  };

  const slotsLeftDisplay = summary.isUnlimitedFsrs
    ? '∞'
    : typeof summary.fsrLimit === 'number'
      ? Math.max(0, summary.fsrLimit - seatsUsed)
      : '—';
  const freePrefix = summary.isFreeAccount ? 'Free account · ' : '';
  const headerSubLine = summary.isUnlimitedFsrs
    ? `${freePrefix}${summary.total} users · Unlimited seats`
    : typeof summary.fsrLimit === 'number'
      ? `${freePrefix}${seatsUsed} of ${summary.fsrLimit} seats used`
      : `${freePrefix}${summary.total} users · No limit on file`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text">Field Assist Users</h1>
          <p className="text-text2">{headerSubLine}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={openAdd}
            disabled={seatGate.blocked}
            title={seatGate.blocked ? seatGate.banner || 'Limit reached' : undefined}
            className="btn-primary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Plus size={18} />
            Add User
          </button>
        </div>
      </div>

      {seatGate.banner && (
        <div
          className={`rounded-xl p-4 text-sm border ${
            seatGate.color === 'red'
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}
        >
          <strong className="font-bold">{seatGate.label}.</strong> {seatGate.banner}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
          <InfoIcon size={18} />
        </div>
        <p className="text-sm text-blue-700">
          <strong>Field reps</strong> use the mobile app to visit retailers and create bookings. Retailers and catalog
          sync automatically from your RiEtail B2B account.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          Couldn't load field users: {error.message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Reps', value: summary.total, color: 'text-text' },
          { label: 'Active', value: summary.active, color: 'text-emerald-600', pulse: true },
          { label: 'Suspended', value: summary.suspended, color: 'text-red-600' },
          { label: 'Seats Left', value: slotsLeftDisplay, color: 'text-amber-600' },
        ].map((stat, i) => (
          <div key={i} className="card p-4 flex flex-col">
            <span className="text-[10px] font-black text-text3 uppercase tracking-widest mb-1">{stat.label}</span>
            <div className="flex items-center gap-2">
              <span className={`text-2xl font-black ${stat.color}`}>{stat.value}</span>
              {stat.pulse && <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
            </div>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div className="p-4 border-b border-border flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-2">
            {(
              [
                { id: 'all', label: `All Reps (${summary.total})` },
                { id: 'direct', label: `Direct Reps (${summary.direct})` },
                { id: 'distributor', label: `Under Distributors (${summary.underDistributor})` },
              ] as { id: FsrTab; label: string }[]
            ).map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all
                  ${tab === id ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-text2 hover:bg-gray-100'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-3 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text3" size={16} />
              <input
                type="text"
                placeholder="Search by name, EMP ID, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 bg-gray-50 border border-border rounded-lg text-sm focus:border-primary outline-none"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-[10px] font-black text-text3 uppercase tracking-widest">
              <tr>
                <th className="px-6 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === fsrs.length && fsrs.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-6 py-3">User</th>
                <th className="px-6 py-3">EMP ID</th>
                <th className="px-6 py-3">Mobile</th>
                <th className="px-6 py-3">Reports To</th>
                <th className="px-6 py-3">Catalog Access</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && fsrs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-text3">
                    Loading field users…
                  </td>
                </tr>
              ) : fsrs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-text3">
                    No field users yet. Click <span className="font-bold">Add User</span> to onboard your first rep.
                  </td>
                </tr>
              ) : (
                fsrs.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-indigo-50/30 transition-colors cursor-pointer group"
                    onClick={() => {
                      setSelectedFsr(user);
                      setIsDetailPanelOpen(true);
                    }}
                  >
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(user.id)}
                        onChange={() => toggleSelect(user.id)}
                        className="rounded border-border text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm"
                          style={{ backgroundColor: user.color || '#4F46E5' }}
                        >
                          {user.avatar || (user.name?.[0] ?? '?').toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-bold text-text">{user.name}</span>
                          <span className="text-[11px] text-text3">{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-text2">{user.employeeId}</td>
                    <td className="px-6 py-4 text-text2">{user.mobile || '—'}</td>
                    <td className="px-6 py-4">
                      {user.reportingTo === 'manufacturer' ? (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-bold uppercase">
                          Direct
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold uppercase truncate max-w-[120px] inline-block">
                          {user.distributorId || 'Distributor'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {user.catalogAccess === 'full' ? (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-bold uppercase">
                          Full Catalog
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] font-bold uppercase">
                          {user.catalogCollections.length} Collections
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <StatusPill status={user.status} />
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setSelectedFsr(user);
                            setIsDetailPanelOpen(true);
                          }}
                          className="p-2 text-text3 hover:text-primary transition-colors rounded-lg hover:bg-white shadow-sm border border-transparent hover:border-border"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => askSuspendToggle(user)}
                          className="p-2 text-text3 hover:text-amber-600 transition-colors rounded-lg hover:bg-white shadow-sm border border-transparent hover:border-border"
                          title={user.status === 'suspended' ? 'Activate' : 'Suspend'}
                        >
                          {user.status === 'suspended' ? <Unlock size={14} /> : <Lock size={14} />}
                        </button>
                        <button
                          onClick={() => askDelete(user)}
                          className="p-2 text-text3 hover:text-red-600 transition-colors rounded-lg hover:bg-white shadow-sm border border-transparent hover:border-border"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk Action Bar */}
      <AnimatePresence>
        {selectedIds.length > 0 && (
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-sidebar text-white px-6 py-3 rounded-2xl shadow-2xl z-40 flex items-center gap-6 border border-white/10"
          >
            <span className="text-sm font-bold">{selectedIds.length} selected</span>
            <div className="w-[1px] h-6 bg-white/10" />
            <div className="flex gap-4">
              <button
                onClick={() => submitBulkStatus('active')}
                className="text-xs font-bold hover:text-emerald-400 transition-colors"
              >
                Activate
              </button>
              <button
                onClick={() => submitBulkStatus('suspended')}
                className="text-xs font-bold hover:text-amber-400 transition-colors"
              >
                Suspend
              </button>
            </div>
            <button onClick={() => setSelectedIds([])} className="p-1 hover:bg-white/10 rounded-full transition-colors">
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Slide Panel */}
      <AnimatePresence>
        {isDetailPanelOpen && selectedFsr && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDetailPanelOpen(false)}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-screen w-[560px] bg-white z-50 shadow-2xl flex flex-col"
            >
              <div className="p-8 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-black text-2xl shadow-lg"
                    style={{ backgroundColor: selectedFsr.color || '#4F46E5' }}
                  >
                    {selectedFsr.avatar || selectedFsr.name[0]}
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-text leading-tight">{selectedFsr.name}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-mono text-text3">{selectedFsr.employeeId}</span>
                      <div className="w-1 h-1 rounded-full bg-text3" />
                      <StatusPill status={selectedFsr.status} />
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsDetailPanelOpen(false)}
                  className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center text-text3 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-8">
                <section>
                  <h3 className="text-[10px] font-black text-text3 uppercase tracking-widest mb-4">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-2xl border border-border flex items-center gap-3">
                      <Mail size={18} className="text-text3" />
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-text3 uppercase">Email</span>
                        <span className="text-sm font-semibold text-text break-all">{selectedFsr.email}</span>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-2xl border border-border flex items-center gap-3">
                      <Phone size={18} className="text-text3" />
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-text3 uppercase">Mobile</span>
                        <span className="text-sm font-semibold text-text">{selectedFsr.mobile || '—'}</span>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-[10px] font-black text-text3 uppercase tracking-widest mb-4">
                    Reporting & Access
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-4 border border-border rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Shield size={18} className="text-primary" />
                        <span className="text-sm font-bold text-text">Reporting To</span>
                      </div>
                      <span className="text-sm font-semibold text-text2">
                        {selectedFsr.reportingTo === 'manufacturer'
                          ? 'Direct (Manufacturer)'
                          : selectedFsr.distributorId || 'Distributor'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-4 border border-border rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Database size={18} className="text-purple-600" />
                        <span className="text-sm font-bold text-text">Catalog Access</span>
                      </div>
                      <span className="text-sm font-semibold text-text2">
                        {selectedFsr.catalogAccess === 'full'
                          ? 'Full Catalog'
                          : `${selectedFsr.catalogCollections.length} Collections`}
                      </span>
                    </div>
                  </div>
                </section>
              </div>

              <div className="p-8 border-t border-border bg-gray-50 flex gap-3">
                <button
                  className="btn-primary flex-1"
                  onClick={() => submitResetPassword(selectedFsr.id)}
                >
                  Reset Password
                </button>
                <button
                  onClick={() => askSuspendToggle(selectedFsr)}
                  className="h-[38px] px-4 bg-amber-50 border border-amber-200 text-amber-700 font-bold text-sm rounded-lg hover:bg-amber-100 transition-all flex-1"
                >
                  {selectedFsr.status === 'suspended' ? 'Activate' : 'Suspend'}
                </button>
                <button
                  onClick={() => askDelete(selectedFsr)}
                  className="h-[38px] px-4 bg-red-50 border border-red-200 text-red-600 font-bold text-sm rounded-lg hover:bg-red-100 transition-all flex-1"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add User Slide Panel */}
      <AnimatePresence>
        {isAddPanelOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !creating && setIsAddPanelOpen(false)}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-screen w-[480px] bg-white z-50 shadow-2xl flex flex-col"
            >
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-black text-text">Add New Field User</h2>
                <button
                  onClick={() => !creating && setIsAddPanelOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-text3 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
                {(createdEmployeeId || createdPassword) && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
                    <p className="text-xs font-black text-emerald-700 uppercase tracking-wider mb-2">
                      User created — credentials emailed
                    </p>
                    <p className="text-xs text-emerald-700 mb-2">
                      A welcome email with these credentials has been sent to the field user. The password is shown
                      here ONCE — copy it now if you need to share manually.
                    </p>
                    <div className="bg-white rounded-lg p-3 font-mono text-xs space-y-1">
                      {createdEmployeeId && (
                        <div>
                          <span className="text-text3">emp id:</span> {createdEmployeeId}
                        </div>
                      )}
                      <div>
                        <span className="text-text3">email:</span> {form.email.toLowerCase()}
                      </div>
                      {createdPassword && (
                        <div>
                          <span className="text-text3">password:</span> {createdPassword}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{formError}</div>
                )}

                <section className="space-y-4">
                  <h3 className="text-xs font-black text-text3 uppercase tracking-widest">Personal Details</h3>
                  <p className="text-[11px] text-text3 -mt-2">
                    Employee ID (EMP-####) is generated automatically on save.
                  </p>
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-field w-full"
                    disabled={!!createdEmployeeId}
                  />
                  <div className="grid grid-cols-1 gap-4">
                    <input
                      type="tel"
                      placeholder="Mobile Number"
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                      className="input-field"
                      disabled={!!createdEmployeeId}
                    />
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input-field"
                      disabled={!!createdEmployeeId}
                    />
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className="text-xs font-black text-text3 uppercase tracking-widest">Reporting Structure</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setForm({ ...form, reportingTo: 'manufacturer' })}
                      disabled={!!createdPassword}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        form.reportingTo === 'manufacturer'
                          ? 'border-primary bg-indigo-50'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
                          form.reportingTo === 'manufacturer' ? 'bg-primary text-white' : 'bg-gray-100 text-text3'
                        }`}
                      >
                        <Shield size={16} />
                      </div>
                      <span className="block font-bold text-sm text-text">Direct Rep</span>
                      <span className="text-[10px] text-text3 leading-tight">Reports to Manufacturer</span>
                    </button>
                    <button
                      onClick={() => setForm({ ...form, reportingTo: 'distributor' })}
                      disabled={!!createdPassword}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        form.reportingTo === 'distributor'
                          ? 'border-primary bg-indigo-50'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
                          form.reportingTo === 'distributor' ? 'bg-primary text-white' : 'bg-gray-100 text-text3'
                        }`}
                      >
                        <Users size={16} />
                      </div>
                      <span className="block font-bold text-sm text-text">Distributor Rep</span>
                      <span className="text-[10px] text-text3 leading-tight">Reports to Distributor</span>
                    </button>
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className="text-xs font-black text-text3 uppercase tracking-widest">Catalog Access</h3>
                  <div className="space-y-3">
                    <label
                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                        form.catalogAccess === 'full' ? 'border-primary bg-indigo-50' : 'border-border hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        checked={form.catalogAccess === 'full'}
                        onChange={() => setForm({ ...form, catalogAccess: 'full' })}
                        className="text-primary focus:ring-primary"
                        disabled={!!createdEmployeeId}
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-text">Full Catalog Access</span>
                        <span className="text-[10px] text-text3">Can view and book all products</span>
                      </div>
                    </label>
                    <label
                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                        form.catalogAccess === 'collection'
                          ? 'border-primary bg-indigo-50'
                          : 'border-border hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        checked={form.catalogAccess === 'collection'}
                        onChange={() => setForm({ ...form, catalogAccess: 'collection' })}
                        className="text-primary focus:ring-primary"
                        disabled={!!createdEmployeeId}
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-text">Collection-wise Access</span>
                        <span className="text-[10px] text-text3">Select specific collections only</span>
                      </div>
                    </label>
                  </div>
                </section>
              </div>

              <div className="p-6 border-t border-border bg-gray-50 flex gap-3">
                {createdEmployeeId ? (
                  <button
                    onClick={() => {
                      setIsAddPanelOpen(false);
                      setCreatedPassword(null);
                      setCreatedEmployeeId(null);
                    }}
                    className="btn-primary flex-1"
                  >
                    Done
                  </button>
                ) : (
                  <>
                    <button onClick={submitCreate} disabled={creating} className="btn-primary flex-1 disabled:opacity-60">
                      {creating ? 'Creating…' : 'Create User'}
                    </button>
                    <button
                      onClick={() => setIsAddPanelOpen(false)}
                      disabled={creating}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Confirmation Dialog (suspend / activate / delete) */}
      <Modal
        isOpen={pending !== null}
        type={pending?.kind === 'delete' ? 'danger' : 'warning'}
        title={
          pending?.kind === 'delete'
            ? 'Delete field user?'
            : pending?.kind === 'suspend'
            ? 'Suspend field user?'
            : 'Activate field user?'
        }
        message={
          pending?.kind === 'delete'
            ? `${pending.fsr.name} (${pending.fsr.employeeId}) will be permanently removed. They will lose access to the mobile app immediately. This cannot be undone.`
            : pending?.kind === 'suspend'
            ? `${pending?.fsr.name} (${pending?.fsr.employeeId}) will lose access to the mobile app until reactivated.`
            : `${pending?.fsr.name} (${pending?.fsr.employeeId}) will be reactivated and can log into the mobile app again.`
        }
        confirmText={
          pending?.kind === 'delete'
            ? 'Delete user'
            : pending?.kind === 'suspend'
            ? 'Suspend'
            : 'Activate'
        }
        cancelText="Cancel"
        onConfirm={confirmPending}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

const InfoIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
